/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ApiResponseFlowMeta } from '../models/api-response-flow-meta';
import { ApiResponseListFlowMeta } from '../models/api-response-list-flow-meta';
import { ApiResponsePagedResultFlowMeta } from '../models/api-response-paged-result-flow-meta';
import { ApiResponsePagedResultFlowMetaResponse } from '../models/api-response-paged-result-flow-meta-response';
import { createAndPublishFlowMeta } from '../fn/flow-meta-api/create-and-publish-flow-meta';
import { CreateAndPublishFlowMeta$Params } from '../fn/flow-meta-api/create-and-publish-flow-meta';
import { createFlowMeta } from '../fn/flow-meta-api/create-flow-meta';
import { CreateFlowMeta$Params } from '../fn/flow-meta-api/create-flow-meta';
import { deleteFlowMetaByRef } from '../fn/flow-meta-api/delete-flow-meta-by-ref';
import { DeleteFlowMetaByRef$Params } from '../fn/flow-meta-api/delete-flow-meta-by-ref';
import { deprecateFlowMetaByRef } from '../fn/flow-meta-api/deprecate-flow-meta-by-ref';
import { DeprecateFlowMetaByRef$Params } from '../fn/flow-meta-api/deprecate-flow-meta-by-ref';
import { exportFlowMetaResponses } from '../fn/flow-meta-api/export-flow-meta-responses';
import { ExportFlowMetaResponses$Params } from '../fn/flow-meta-api/export-flow-meta-responses';
import { exportFlowMetaWithResponses } from '../fn/flow-meta-api/export-flow-meta-with-responses';
import { ExportFlowMetaWithResponses$Params } from '../fn/flow-meta-api/export-flow-meta-with-responses';
import { getFlowMetaByRef } from '../fn/flow-meta-api/get-flow-meta-by-ref';
import { GetFlowMetaByRef$Params } from '../fn/flow-meta-api/get-flow-meta-by-ref';
import { getFlowMetaResponses } from '../fn/flow-meta-api/get-flow-meta-responses';
import { GetFlowMetaResponses$Params } from '../fn/flow-meta-api/get-flow-meta-responses';
import { listFlowMeta } from '../fn/flow-meta-api/list-flow-meta';
import { ListFlowMeta$Params } from '../fn/flow-meta-api/list-flow-meta';
import { previewFlowMeta } from '../fn/flow-meta-api/preview-flow-meta';
import { PreviewFlowMeta$Params } from '../fn/flow-meta-api/preview-flow-meta';
import { searchFlowMeta } from '../fn/flow-meta-api/search-flow-meta';
import { SearchFlowMeta$Params } from '../fn/flow-meta-api/search-flow-meta';
import { updateFlowMeta } from '../fn/flow-meta-api/update-flow-meta';
import { UpdateFlowMeta$Params } from '../fn/flow-meta-api/update-flow-meta';

@Injectable({ providedIn: 'root' })
export class FlowMetaApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getFlowMetaByRef()` */
  static readonly GetFlowMetaByRefPath = '/v1/api/ui/flow-meta/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getFlowMetaByRef()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFlowMetaByRef$Response(params: GetFlowMetaByRef$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseFlowMeta>> {
    return getFlowMetaByRef(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getFlowMetaByRef$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFlowMetaByRef(params: GetFlowMetaByRef$Params, context?: HttpContext): Observable<ApiResponseFlowMeta> {
    return this.getFlowMetaByRef$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseFlowMeta>): ApiResponseFlowMeta => r.body)
    );
  }

  /** Path part for operation `updateFlowMeta()` */
  static readonly UpdateFlowMetaPath = '/v1/api/ui/flow-meta/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateFlowMeta()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFlowMeta$Response(params: UpdateFlowMeta$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseFlowMeta>> {
    return updateFlowMeta(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateFlowMeta$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFlowMeta(params: UpdateFlowMeta$Params, context?: HttpContext): Observable<ApiResponseFlowMeta> {
    return this.updateFlowMeta$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseFlowMeta>): ApiResponseFlowMeta => r.body)
    );
  }

  /** Path part for operation `deleteFlowMetaByRef()` */
  static readonly DeleteFlowMetaByRefPath = '/v1/api/ui/flow-meta/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteFlowMetaByRef()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteFlowMetaByRef$Response(params: DeleteFlowMetaByRef$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteFlowMetaByRef(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteFlowMetaByRef$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteFlowMetaByRef(params: DeleteFlowMetaByRef$Params, context?: HttpContext): Observable<void> {
    return this.deleteFlowMetaByRef$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `createFlowMeta()` */
  static readonly CreateFlowMetaPath = '/v1/api/ui/flow-meta';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createFlowMeta()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createFlowMeta$Response(params: CreateFlowMeta$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseFlowMeta>> {
    return createFlowMeta(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createFlowMeta$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createFlowMeta(params: CreateFlowMeta$Params, context?: HttpContext): Observable<ApiResponseFlowMeta> {
    return this.createFlowMeta$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseFlowMeta>): ApiResponseFlowMeta => r.body)
    );
  }

  /** Path part for operation `createAndPublishFlowMeta()` */
  static readonly CreateAndPublishFlowMetaPath = '/v1/api/ui/flow-meta/sync-flow-meta';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAndPublishFlowMeta()` instead.
   *
   * This method doesn't expect any request body.
   */
  createAndPublishFlowMeta$Response(params: CreateAndPublishFlowMeta$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseFlowMeta>> {
    return createAndPublishFlowMeta(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAndPublishFlowMeta$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  createAndPublishFlowMeta(params: CreateAndPublishFlowMeta$Params, context?: HttpContext): Observable<ApiResponseFlowMeta> {
    return this.createAndPublishFlowMeta$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseFlowMeta>): ApiResponseFlowMeta => r.body)
    );
  }

  /** Path part for operation `searchFlowMeta()` */
  static readonly SearchFlowMetaPath = '/v1/api/ui/flow-meta/search';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchFlowMeta()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchFlowMeta$Response(params: SearchFlowMeta$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePagedResultFlowMeta>> {
    return searchFlowMeta(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchFlowMeta$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchFlowMeta(params: SearchFlowMeta$Params, context?: HttpContext): Observable<ApiResponsePagedResultFlowMeta> {
    return this.searchFlowMeta$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePagedResultFlowMeta>): ApiResponsePagedResultFlowMeta => r.body)
    );
  }

  /** Path part for operation `getFlowMetaResponses()` */
  static readonly GetFlowMetaResponsesPath = '/v1/api/ui/flow-meta/responses';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getFlowMetaResponses()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getFlowMetaResponses$Response(params: GetFlowMetaResponses$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePagedResultFlowMetaResponse>> {
    return getFlowMetaResponses(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getFlowMetaResponses$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getFlowMetaResponses(params: GetFlowMetaResponses$Params, context?: HttpContext): Observable<ApiResponsePagedResultFlowMetaResponse> {
    return this.getFlowMetaResponses$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePagedResultFlowMetaResponse>): ApiResponsePagedResultFlowMetaResponse => r.body)
    );
  }

  /** Path part for operation `exportFlowMetaResponses()` */
  static readonly ExportFlowMetaResponsesPath = '/v1/api/ui/flow-meta/responses/export/{lang}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportFlowMetaResponses()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  exportFlowMetaResponses$Response(params: ExportFlowMetaResponses$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return exportFlowMetaResponses(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportFlowMetaResponses$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  exportFlowMetaResponses(params: ExportFlowMetaResponses$Params, context?: HttpContext): Observable<Blob> {
    return this.exportFlowMetaResponses$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `exportFlowMetaWithResponses()` */
  static readonly ExportFlowMetaWithResponsesPath = '/v1/api/ui/flow-meta/export-all/{lang}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportFlowMetaWithResponses()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  exportFlowMetaWithResponses$Response(params: ExportFlowMetaWithResponses$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return exportFlowMetaWithResponses(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportFlowMetaWithResponses$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  exportFlowMetaWithResponses(params: ExportFlowMetaWithResponses$Params, context?: HttpContext): Observable<Blob> {
    return this.exportFlowMetaWithResponses$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `deprecateFlowMetaByRef()` */
  static readonly DeprecateFlowMetaByRefPath = '/v1/api/ui/flow-meta/deprecate/{reference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deprecateFlowMetaByRef()` instead.
   *
   * This method doesn't expect any request body.
   */
  deprecateFlowMetaByRef$Response(params: DeprecateFlowMetaByRef$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deprecateFlowMetaByRef(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deprecateFlowMetaByRef$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deprecateFlowMetaByRef(params: DeprecateFlowMetaByRef$Params, context?: HttpContext): Observable<void> {
    return this.deprecateFlowMetaByRef$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `previewFlowMeta()` */
  static readonly PreviewFlowMetaPath = '/v1/api/ui/flow-meta/preview/{reference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `previewFlowMeta()` instead.
   *
   * This method doesn't expect any request body.
   */
  previewFlowMeta$Response(params: PreviewFlowMeta$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseFlowMeta>> {
    return previewFlowMeta(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `previewFlowMeta$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  previewFlowMeta(params: PreviewFlowMeta$Params, context?: HttpContext): Observable<ApiResponseFlowMeta> {
    return this.previewFlowMeta$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseFlowMeta>): ApiResponseFlowMeta => r.body)
    );
  }

  /** Path part for operation `listFlowMeta()` */
  static readonly ListFlowMetaPath = '/v1/api/ui/flow-meta/list';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listFlowMeta()` instead.
   *
   * This method doesn't expect any request body.
   */
  listFlowMeta$Response(params: ListFlowMeta$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseListFlowMeta>> {
    return listFlowMeta(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listFlowMeta$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listFlowMeta(params: ListFlowMeta$Params, context?: HttpContext): Observable<ApiResponseListFlowMeta> {
    return this.listFlowMeta$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseListFlowMeta>): ApiResponseListFlowMeta => r.body)
    );
  }

}
